import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    IconButton,
    Button,
} from '@mui/material';
import {
    LocationOnOutlined,
    PersonOutlined,
    ExpandMore,
    EmailOutlined,
    PhoneOutlined,
    DownloadOutlined,
} from '@mui/icons-material';
import { ReactComponent as LTLogo } from '/public/logo.svg';
import { withTranslation } from 'react-i18next';
import {
    getAssignmentWelcomePacket,
    downloadDocumentWCAPI,
} from '../../../services/DocumentService';
import { DOCUMENT_FIELD_NAME } from '../../../utils/Application_Constants';
import { getNestedProperty } from '../../../utils/helpers';

const DayOneDetailsTab = ({ assignment, assignmentTeam, t }) => {
    const [expandedContact, setExpandedContact] = useState(null);
    const [welcomePacketDocument, setWelcomePacketDocument] = useState(null);
    const [isLoadingWelcomePacket, setIsLoadingWelcomePacket] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const { onboarding } = assignment;
    const DEFAULT_IMAGE_LENGTH = 15551; // Default image length for Meet Your Team avatar

    useEffect(() => {
        const fetchWelcomePacket = async () => {
            try {
                setIsLoadingWelcomePacket(true);

                const document = await getAssignmentWelcomePacket(
                    assignment.clinicianId,
                    assignment.id
                );

                setWelcomePacketDocument(document);
            } catch (error) {
                console.error('Error fetching data:', error);
                setWelcomePacketDocument(null);
            } finally {
                setIsLoadingWelcomePacket(false);
            }
        };

        fetchWelcomePacket();
    }, []);

    const handleContactExpand = (contactId) => (event, isExpanded) => {
        setExpandedContact(isExpanded ? contactId : null);
    };

    const handleDownloadWelcomePacket = async () => {
        if (!welcomePacketDocument || !assignment.clinicianId) {
            return;
        }

        try {
            setIsDownloading(true);
            await downloadDocumentWCAPI(
                welcomePacketDocument,
                getNestedProperty(welcomePacketDocument, DOCUMENT_FIELD_NAME.DocumentType)
            );
        } catch (error) {
            console.error('Error downloading welcome packet:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName.charAt(0).toUpperCase() : '';
        const last = lastName ? lastName.charAt(0).toUpperCase() : '';
        return first + last;
    };

    const renderInfoSection = (title, content, isBlue = false, icon = null) => {
        if (!content) return null;

        // Process content to handle bold markdown-style formatting
        const processContent = (text) => {
            const parts = text.split(/(\*\*[^*]+\*\*)/);
            return parts.map((part, index) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={index}>{part.slice(2, -2)}</strong>;
                }
                return part;
            });
        };

        return (
            <Box sx={{ mb: 2, position: 'relative' }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: isBlue ? 'primary.main' : 'text.primary',
                    }}
                >
                    {title}
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
                    {processContent(content)}
                </Typography>
                {icon && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {React.cloneElement(icon, {
                            sx: { color: 'primary.main', fontSize: 20 },
                        })}
                    </Box>
                )}
            </Box>
        );
    };

    const renderCard = (title, sections) => (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                    {title}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {sections.map((section) => (
                    <React.Fragment key={section.title}>
                        {renderInfoSection(
                            section.title,
                            section.content,
                            section.isBlue,
                            section.icon
                        )}
                    </React.Fragment>
                ))}
            </CardContent>
        </Card>
    );

    const renderWelcomePacketButton = () => {
        if (isLoadingWelcomePacket || !welcomePacketDocument) {
            return null;
        }

        return (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadOutlined />}
                    onClick={handleDownloadWelcomePacket}
                    disabled={isDownloading}
                    sx={{ px: 4, py: 1.5, minWidth: '280px' }}
                >
                    {isDownloading ? 'Downloading...' : 'Download Welcome Packet'}
                </Button>
            </Box>
        );
    };

    const renderWhenYouArriveCard = () => {
        const hasArrivalTime = onboarding?.whenToReport;
        const hasArrivalDirections = onboarding?.whereToReport;
        const hasGreeter = onboarding?.greeter;

        if (!hasArrivalTime && !hasArrivalDirections && !hasGreeter) {
            return null;
        }

        const sections = [];

        if (hasArrivalTime || hasArrivalDirections) {
            let arrivalContent = '';
            if (hasArrivalTime) {
                arrivalContent += `**${hasArrivalTime}**`;
            }
            if (hasArrivalDirections) {
                if (hasArrivalTime) {
                    arrivalContent += '\n';
                }
                arrivalContent += hasArrivalDirections;
            }

            sections.push({
                title: 'Arrival',
                content: arrivalContent,
                isBlue: true,
                icon: <LocationOnOutlined />,
            });
        }

        if (hasGreeter) {
            sections.push({
                title: 'Greeter',
                content: hasGreeter,
                isBlue: true,
                icon: <PersonOutlined />,
            });
        }

        return (
            <Grid item xs={12}>
                {renderCard('When You Arrive', sections)}
            </Grid>
        );
    };

    const renderContactAvatar = (contact) => {
        if (contact.id === 'support') {
            return (
                <LTLogo
                    style={{
                        width: 40,
                        height: 40,
                    }}
                />
            );
        }

        if (contact.image) {
            if (contact.image.length !== DEFAULT_IMAGE_LENGTH) {
                return <Avatar src={contact.image} sx={{ width: 40, height: 40 }} />;
            }
        }

        return (
            <Avatar
                sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'grey.300',
                    color: 'grey.700',
                    fontSize: '0.875rem',
                }}
            >
                {getInitials(contact.firstName, contact.lastName)}
            </Avatar>
        );
    };

    const renderPeopleToKnowCard = () => {
        const contacts = [];

        contacts.push({
            id: 'support',
            title: 'LocumTenens.com',
            firstName: '24/7',
            lastName: 'Support',
            phones: { mobile: t('common:INFORMATION.HELP_NUMBER') },
            emails: {},
        });

        const recruiter = assignmentTeam.find((obj) => obj.role === 'Recruiter');
        if (recruiter) {
            contacts.push({
                ...recruiter,
                id: 'recruiter',
                title: 'Recruiter',
            });
        }

        const crs = assignmentTeam.find((obj) => obj.role === 'Clinician Deployment Specialist');
        const customerCare = assignmentTeam.find((obj) => obj.role === 'Customer Care Specialist');

        if (crs) {
            contacts.push({
                ...crs,
                id: 'clinicianDeploymentSpecialist',
                title: 'Clinician Relations Specialist',
            });
        } else if (customerCare) {
            contacts.push({
                ...customerCare,
                id: 'customerCareSpecialist',
                title: 'Customer Care Specialist',
            });
        }

        if (contacts.length === 0) {
            return null;
        }

        return (
            <Grid item xs={12}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                            People to Know
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            These are the LT Associates that will be your main points of contact for
                            your assignment. They will be able to assist with anything you may need
                            while working with us.
                        </Typography>

                        {contacts.map((contact) => (
                            <Accordion
                                key={contact.id}
                                expanded={expandedContact === contact.id}
                                onChange={handleContactExpand(contact.id)}
                                sx={{ mb: 1, '&:last-child': { mb: 0 } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMore />} sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {renderContactAvatar(contact)}
                                        <Box>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {contact.firstName} {contact.lastName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {contact.title}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            justifyContent: 'space-evenly',
                                        }}
                                    >
                                        {contact.emails && contact.emails.primary && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    backgroundColor: 'grey.100',
                                                    borderRadius: '9999px',
                                                    px: { xs: 4, sm: 8, md: 12, lg: 16 },
                                                    py: 0.5,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    href={`mailto:${contact.emails.primary}`}
                                                    sx={{ color: 'primary.main' }}
                                                >
                                                    <EmailOutlined fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                        {contact.phones && contact.phones.mobile && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    backgroundColor: 'grey.100',
                                                    borderRadius: '9999px',
                                                    px: { xs: 4, sm: 8, md: 12, lg: 16 },
                                                    py: 0.5,
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    href={`tel:${contact.phones.mobile}`}
                                                    sx={{
                                                        color: 'primary.main',
                                                    }}
                                                >
                                                    <PhoneOutlined fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    const renderParkingCard = () => {
        const parkingInfo = onboarding?.parkingInstructions;

        if (!parkingInfo) {
            return null;
        }

        return (
            <Grid item xs={12}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                            Parking
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line' }}
                        >
                            {parkingInfo}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    const renderGeneralInfoCard = () => {
        const orientationProvided = onboarding?.onsiteOrientationRequired;
        const orientationInfo = onboarding?.orientationInformation;

        const orientationText = [orientationProvided, orientationInfo].filter(Boolean).join('. ');

        let formattedOrientationText = null;

        if (orientationText) {
            const trimmed = orientationText.trim();
            formattedOrientationText = trimmed.endsWith('.') ? trimmed : `${trimmed}.`;
        }

        const sections = [
            {
                title: 'Pager / Phone',
                content: onboarding?.pagerOrPhoneProvided,
            },
            {
                title: 'Orientation',
                content: formattedOrientationText,
            },
            {
                title: 'Tour',
                content: onboarding?.tourProvided,
            },
            {
                title: 'EMR/IT Training Requirements',
                content: onboarding?.emrOrItTrainingRequirements,
            },
        ];

        const sectionsWithContent = sections.filter((section) => section.content);

        if (sectionsWithContent.length === 0) {
            return null;
        }

        return (
            <Grid item xs={12}>
                {renderCard('General Information', sectionsWithContent)}
            </Grid>
        );
    };

    return (
        <Box sx={{ mt: 2 }}>
            {renderWelcomePacketButton()}
            <Grid container spacing={3}>
                {renderWhenYouArriveCard()}
                {renderGeneralInfoCard()}
                {renderParkingCard()}
                {renderPeopleToKnowCard()}
            </Grid>
        </Box>
    );
};

export default withTranslation()(DayOneDetailsTab);
